import * as React from 'react'

type UseZoomMenuDismissArgs = {
  open: boolean
  containerRef: React.RefObject<HTMLDivElement | null>
  dropdownPanelRef: React.RefObject<HTMLDivElement | null>
  setOpen: (open: boolean) => void
  setIsDirty: (dirty: boolean) => void
  dirtyFromInputChangeRef: React.MutableRefObject<boolean>
  resetDraftFromProps: () => void
  setHighlightIndex: (index: number) => void
  inputRef: React.RefObject<HTMLInputElement | null>
}

/**
 * While open: a pointerdown outside the control and its (portaled) panel closes the menu; Escape
 * closes it and discards the draft.
 */
export function useZoomMenuDismiss({
  open,
  containerRef,
  dropdownPanelRef,
  setOpen,
  setIsDirty,
  dirtyFromInputChangeRef,
  resetDraftFromProps,
  setHighlightIndex,
  inputRef,
}: UseZoomMenuDismissArgs): void {
  React.useEffect(() => {
    if (!open) return

    const handlePointerDown = (event: PointerEvent) => {
      const { target } = event

      if (target instanceof Node) {
        if (containerRef.current?.contains(target)) return
        if (dropdownPanelRef.current?.contains(target)) return
      }

      setOpen(false)
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Claims the press so a panel Escape chain doesn't also close its panel (WMPCM-12349).
        event.preventDefault()
        setOpen(false)
        setIsDirty(false)
        dirtyFromInputChangeRef.current = false
        resetDraftFromProps()
        setHighlightIndex(-1)
        inputRef.current?.blur()
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    // Window capture runs before every document-level listener, so the claim above is seen no
    // matter when the panel's own listener registered.
    window.addEventListener('keydown', handleKeyDown, true)

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      window.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [
    open,
    resetDraftFromProps,
    containerRef,
    dropdownPanelRef,
    dirtyFromInputChangeRef,
    inputRef,
    setHighlightIndex,
    setIsDirty,
    setOpen,
  ])
}
